/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useFetcher, useLocation, useParams } from '@remix-run/react'
import { motion, AnimatePresence } from 'framer-motion'
import { type TFunction } from 'i18next'
import {
	SquarePen,
	MoreHorizontal,
	Trash2,
	Settings,
	LucideSettings,
	Mail,
	User,
	Pencil,
} from 'lucide-react'
import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { LocaleLink } from '#app/components/locale-link.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import {
	Menubar,
	MenubarContent,
	MenubarItem,
	MenubarMenu,
	MenubarTrigger,
} from '#app/components/ui/menubar.tsx'

import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { SET_IS_MENU_VISIBLE, SET_TYPE_OF_VIEW } from '#app/constants/chat.ts'
import { useCurrentLanguage } from '#app/hooks/use-current-language.tsx'
import { useCustomNavigate } from '#app/hooks/use-custom-navigate.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'

const listVariants = {
	initial: {
		opacity: 0,
		y: -20,
	},
	animate: {
		opacity: 1,
		y: 0,
		transition: {
			type: 'spring',
			stiffness: 260,
			damping: 20,
		},
	},
	exit: {
		opacity: 0,
		y: 20,
		transition: {
			duration: 0.5,
		},
	},
}

interface Conversation {
	id: string
	title: string
	updatedAt: string
}

interface ConversationGroup {
	title: string
	items: Conversation[]
}

interface GroupedConversations {
	today: ConversationGroup
	yesterday: ConversationGroup
	last7Days: ConversationGroup
	last30Days: ConversationGroup
}

const groupConversations = (
	conversations: Conversation[],
	t: TFunction,
): GroupedConversations => {
	const groups: GroupedConversations = {
		today: { title: t('menu.today'), items: [] },
		yesterday: { title: t('menu.yesterday'), items: [] },
		last7Days: { title: t('menu.last7days'), items: [] },
		last30Days: { title: t('menu.last30days'), items: [] },
	}

	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const yesterday = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 1,
	)
	const last7Days = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 7,
	)
	const last30Days = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 30,
	)

	conversations.forEach(conversation => {
		const updatedAt = new Date(conversation.updatedAt)
		if (updatedAt >= today) {
			groups.today.items.push(conversation)
		} else if (updatedAt >= yesterday && updatedAt < today) {
			groups.yesterday.items.push(conversation)
		} else if (updatedAt >= last7Days && updatedAt < yesterday) {
			groups.last7Days.items.push(conversation)
		} else if (updatedAt >= last30Days && updatedAt < last7Days) {
			groups.last30Days.items.push(conversation)
		}
	})

	return groups
}

interface HandleEditSubmitProps {
	conversation: string
	title: string
	e: { preventDefault: () => void }
}

const Sidebar = () => {
	const { t } = useTranslation('chat')
	const conversationList = useGlobalChatStore(
		useShallow(state => state.conversationList),
	)
	const dispatch = useGlobalChatStore(useShallow(state => state.dispatch))
	const isCurrentlyFetchingMoreConversations = useGlobalChatStore(
		state => state.isCurrentlyFetchingMoreConversations,
	)
	const totalConversations = useGlobalChatStore(
		state => state.totalConversations,
	)
	const currentPagesLoaded = useGlobalChatStore(
		state => state.currentPagesLoaded,
	)

	const totalCoins = useGlobalChatStore(state => state.coinsLeft)

	const titleHasBeenSet = useGlobalChatStore(state => state.titleHasBeenSet)

	const sortedConversations = conversationList.sort(
		(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
	)
	const conversationsPerPage = 20
	const groupedConversations = groupConversations(sortedConversations, t)

	const currentLanguage = useCurrentLanguage()
	const navigate = useCustomNavigate()
	const { param } = useParams()
	const location = useLocation()

	const deleteFetcher: any = useFetcher()

	const [fetcherEditKey, setFetcherEditKey] = useState('edit')
	const editFetcher: any = useFetcher()
	const loadMoreFetcher: any = useFetcher({
		key: `loadMoreConversations-${currentPagesLoaded}`,
	})

	const [isRenaming, setIsRenaming] = useState(false)
	const [renameId, setRenameId] = useState(null)
	const [renameValue, setRenameValue] = useState('')
	const [originalValue, setOriginalValue] = useState('')

	const scrollContainerRef = useRef(null)
	const endOfListRef = useRef(null)

	const handleEditSubmit = ({
		e,
		conversation,
		title,
	}: HandleEditSubmitProps) => {
		e.preventDefault()

		const formData = new FormData()
		formData.append('_action', 'edit_title')
		formData.append('conversationId', conversation)
		formData.append('title', renameValue)

		setIsRenaming(false)
		setRenameId(null)
		setOriginalValue(title)

		dispatch({
			type: 'SET_TITLE',
			title: renameValue,
			conversationId: conversation,
			editOrCreate: 'edit',
		})

		editFetcher.submit(formData, {
			method: 'post',
		})
	}

	useEffect(() => {
		if (editFetcher.data && editFetcher.data.type === 'title_edited') {
			setFetcherEditKey(fetcherEditKey + 1)
		}
	}, [dispatch, titleHasBeenSet, editFetcher])

	useEffect(() => {
		if (
			loadMoreFetcher.data &&
			loadMoreFetcher.data.type === 'set_more_conversations' &&
			isCurrentlyFetchingMoreConversations
		) {
			dispatch({
				type: 'ADD_CONVERSATION_LIST',
				payload: loadMoreFetcher.data.results.data,
				typeOfDispatch: 'add',
				meta: loadMoreFetcher.data.results.meta,
			})
		}
	}, [dispatch, isCurrentlyFetchingMoreConversations, loadMoreFetcher])

	// Hier gaat het mis omdat we alles in 1 keer ophalen
	const loadMoreConversations = () => {
		const hasFetchedAllItems = conversationList.length === totalConversations

		if (loadMoreFetcher.state === 'submitting' && !hasFetchedAllItems) return

		const remainingConversations =
			totalConversations - currentPagesLoaded * conversationsPerPage

		if (remainingConversations <= 0) return

		const formData = new FormData()

		const start = currentPagesLoaded * conversationsPerPage

		formData.append('_action', 'load_more_conversations')
		formData.append('start', `${start}`)

		loadMoreFetcher.submit(formData, {
			method: 'post',
		})

		dispatch({ type: 'SET_IS_CURRENTLY_FETCHING', isCurrentlyFetching: true })
	}

	const handleScroll = (e: any) => {
		const hasFetchedAllItems = conversationList.length === totalConversations

		const { scrollTop, clientHeight, scrollHeight } = e.currentTarget

		const threshold = 10

		if (
			scrollHeight - scrollTop - clientHeight < threshold &&
			!hasFetchedAllItems
		) {
			if (!isCurrentlyFetchingMoreConversations) {
				loadMoreConversations()
			}
		}
	}

	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const hasFetchedAllItems =
							conversationList.length === totalConversations

						if (!hasFetchedAllItems && !isCurrentlyFetchingMoreConversations) {
							loadMoreConversations()
						}
					}
				})
			},
			{
				rootMargin: '100px',
			},
		)

		if (endOfListRef.current) {
			observer.observe(endOfListRef.current)
		}

		return () => observer.disconnect()
	}, [conversationList])

	return (
		<div className="scrollbar-trigger z-50 flex h-full w-full flex-1 items-start border-white/20">
			<nav className="flex h-full flex-1 flex-col space-y-1 p-1 ">
				<div className=" px-3">
					<LocaleLink
						lang={currentLanguage}
						route="chat"
						onClick={() => {
							if (location.pathname === `/${currentLanguage}/chat`) {
								return window.location.reload()
							}

							dispatch({
								type: 'DELETE_MESSAGE',
							})
							dispatch({ type: SET_IS_MENU_VISIBLE, isVisible: false })
							dispatch({ type: 'SET_TYPE_OF_VIEW', typeOfView: 'session' })
						}}
					>
						<a className="mt-3 flex flex-shrink-0 cursor-pointer items-center justify-between  rounded-md p-3 px-3 text-sm text-gray-900 transition-colors duration-200 hover:bg-gray-500/10">
							<img
								alt="Wiser logo"
								src="/img/wiser-logo-small.png"
								className=" -my-2 h-8 w-auto"
							/>
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<SquarePen className="h-5 w-auto text-primary-400" />
									</TooltipTrigger>
									<TooltipContent>
										<p>{t('menu.newChat')}</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</a>
					</LocaleLink>
				</div>

				<div
					ref={scrollContainerRef}
					className="-pt-10 hide-scrollbar  show-scrollbar-on-hover flex-1 flex-col overflow-y-scroll border-b border-white/20 p-3"
					onScroll={e => {
						handleScroll(e)
					}}
				>
					{Object.values(groupedConversations).map(
						group =>
							group.items.length > 0 && (
								<div key={group.title} className="my-3">
									<h2 className="mb-2 text-sm font-bold text-gray-600">
										{group.title}
									</h2>
									<AnimatePresence>
										{group.items.map((conversation: any, index: any) => (
											<motion.div
												key={conversation.id}
												initial="initial"
												animate="animate"
												exit="exit"
												variants={listVariants}
											>
												{isRenaming && renameId === conversation.id ? (
													<editFetcher.Form
														className=" px-3 py-2"
														onSubmit={(e: any) => {
															handleEditSubmit({
																e,
																conversation: conversation.id,
																title: conversation.title,
															})
														}}
													>
														<input
															autoFocus
															type="text"
															value={renameValue}
															onChange={e => setRenameValue(e.target.value)}
															onBlur={e => {
																if (renameValue !== originalValue) {
																	handleEditSubmit({
																		e,
																		conversation: conversation.id,
																		title: conversation.title,
																	})
																}

																setIsRenaming(false)
															}}
															className="border-2 border-primary-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-400"
														/>
													</editFetcher.Form>
												) : (
													<LocaleLink
														lang={currentLanguage}
														route="individualChat"
														isChildOf="chat"
														params={{ param: conversation.id }}
														onClick={e => {
															if (e.defaultPrevented) {
																e.stopPropagation()
																return
															}

															dispatch({
																type: SET_IS_MENU_VISIBLE,
																isVisible: false,
															})

															dispatch({
																type: SET_TYPE_OF_VIEW,
																typeOfView: 'chat',
															})
														}}
													>
														<div
															className={`group relative -my-1 flex cursor-pointer items-center gap-3 break-all rounded-md px-3 py-[0.6rem] hover:bg-gray-300 hover:pr-4 ${
																param === conversation.id ? 'bg-white' : ''
															}`}
														>
															<div className="group relative max-h-5 flex-1 overflow-hidden text-ellipsis break-all text-[0.9rem] font-medium text-primary-400">
																{conversation.title}
																<div
																	className={`absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l group-hover:w-full ${
																		param === conversation.id
																			? 'w-[80%] from-white from-15%'
																			: 'from-[#ebeae8] from-15%'
																	}   group-hover:from-gray-300 `}
																/>
																<div
																	className={`absolute inset-y-0 right-0 z-20 flex w-8 items-center justify-center opacity-0 ${
																		param === conversation.id
																			? 'opacity-100'
																			: ''
																	} group-hover:opacity-100`}
																>
																	<Menubar>
																		<MenubarMenu>
																			<MenubarTrigger>
																				<TooltipProvider>
																					<Tooltip>
																						<TooltipTrigger
																							asChild
																							className="z-40 -m-2"
																							onClick={e => {
																								e.stopPropagation()
																								e.preventDefault()
																							}}
																						>
																							<MoreHorizontal className="hover:text-primary-300" />
																						</TooltipTrigger>
																						<TooltipContent>
																							<p>{t('menu.more')}</p>
																						</TooltipContent>
																					</Tooltip>
																				</TooltipProvider>
																			</MenubarTrigger>
																			<MenubarContent style={{ zIndex: 101 }}>
																				<MenubarItem
																					onClick={e => {
																						e.stopPropagation()
																						const formData = new FormData()
																						formData.append(
																							'_action',
																							'delete_conversation',
																						)
																						formData.append(
																							'conversationId',
																							conversation.id,
																						)

																						formData.append(
																							'currentParam',
																							`${param}`,
																						)

																						deleteFetcher.submit(formData, {
																							method: 'post',
																							navigate: false,
																						})

																						dispatch({
																							type: 'ADD_CONVERSATION_LIST',
																							typeOfDispatch: 'delete',
																							conversationId: conversation.id,
																						})

																						if (conversation.id === param) {
																							dispatch({
																								type: 'DELETE_MESSAGE',
																							})

																							navigate({ route: 'chat' })
																						}
																					}}
																					className="cursor-pointer gap-2 font-medium "
																				>
																					<Trash2 className="text-red-500 hover:text-red-500" />{' '}
																					<p className="text-red-500 hover:text-red-500">
																						{t('menu.deleteChat')}
																					</p>
																				</MenubarItem>
																				<MenubarItem
																					onClick={e => {
																						e.stopPropagation()
																						setIsRenaming(true)
																						setRenameId(conversation.id)
																						setRenameValue(conversation.title)
																					}}
																					className="cursor-pointer gap-2 font-medium "
																				>
																					<Pencil /> <p>{t('menu.rename')}</p>
																				</MenubarItem>
																			</MenubarContent>
																		</MenubarMenu>
																	</Menubar>
																</div>
															</div>
														</div>
													</LocaleLink>
												)}
											</motion.div>
										))}
									</AnimatePresence>
								</div>
							),
					)}
					<div ref={endOfListRef}></div>
					{/* </div> */}
				</div>
				<div className="mb-10 lg:hidden">
					<DropdownMenu>
						<DropdownMenuTrigger className="w-full focus:outline-none focus:ring focus:ring-[#ebeae8]">
							{' '}
							<div className="flex cursor-pointer items-center gap-3  rounded-md px-3 text-sm text-primary-400 transition-colors duration-200 hover:bg-gray-500/10">
								<p className="flex flex-row gap-2 p-3 text-sm font-bold">
									<Settings className="-mt-[0.1rem]" />
									{t('menu.settings')}
								</p>
							</div>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="my-3 w-[16rem] cursor-pointer gap-2 rounded-xl text-primary-400">
							<DropdownMenuLabel className="flex justify-between font-bold">
								<p className="flex flex-row gap-1">
									{' '}
									{t('menu.totalCoinsLeft')}:
								</p>
								{totalCoins}
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => navigate({ route: 'settings' })}>
								<LucideSettings />
								{t('menu.yourSettings')}
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => {
									navigate({
										route: 'profileBigFiveOverview',
										childRoute: 'profileOverview',
									})
								}}
							>
								<User />
								{t('menu.yourProfile')}
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => navigate({ route: 'contactWiser' })}
							>
								<Mail />
								{t('menu.contactUs')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</nav>
		</div>
	)
}

export default memo(Sidebar)
